import { useQueryClient } from '@tanstack/react-query';
import { MILESTONES } from 'lib/constants';
import { isMilestoneCompleted } from 'lib/utils';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { PATH_ROUTES } from 'routes/path';
import { usePostOnboardingStep } from 'services/dashboard/hooks';
import {
  usePostQuestionnaireV3,
  useUpdateQuestionnaireV3,
} from 'services/profile/hooks';
import { QuestionnaireParams } from 'services/profile/type';
import { postHealthRisksQuestions } from 'services/risk-checker/api';
import { useExecuteRiskEngine } from 'services/risk-checker/hooks';
import { useQuestionnaireStore } from 'store/questionnaire';
import useRiskCheckerStore from 'store/risk-checker';

interface SubmitProps {
  questionData: QuestionnaireParams;
  choice_value: string | unknown;
  spanish_choice_value: string | unknown;
  code?: string;
}

export default function useQuestionnaire() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const questionCode = searchParams.get('code');

  const queryClient = useQueryClient();

  const setQuestionnaireData = useQuestionnaireStore(
    (store) => store.setQuestionData
  );
  const engineName = useRiskCheckerStore((store) => store.engineName);

  const [isUpdating, setIsUpdating] = useState(false);

  const { mutateAsync: postQuestionnaire } = usePostQuestionnaireV3();
  const { mutateAsync: updateQuestionnaire } = useUpdateQuestionnaireV3();
  const { mutateAsync: updateOnboarding } = usePostOnboardingStep();
  const { mutateAsync: executeRiskEngineMutation } = useExecuteRiskEngine();

  const onSubmit = async ({
    questionData,
    choice_value,
    spanish_choice_value,
    code,
  }: SubmitProps) => {
    setIsUpdating(true);
    try {
      const payloadData = {
        choice_value,
        spanish_choice_value,
        question_sequence: questionData?.question_sequence || 1,
        answer_id: questionData?.answer_id || null,
        question_id: questionData?.q_id || '',
        isEdit: questionData?.isEdit || false,
        ...(code && { code }),
      };
      if (engineName) {
        const res = await postHealthRisksQuestions({
          data: payloadData,
          scanFlag: false,
        });
        console.log('🚀 ~ onSubmit ~ res:', res);
        if (res.screen_name === 'generate_risk_score') {
          await executeRiskEngineMutation({
            risk_type: engineName,
          });
          return navigate(PATH_ROUTES.RISK_ANALYSIS_DETAIL);
        }

        const questionItem = res?.data || null;
        setQuestionnaireData(questionItem as any);
      } else {
        const isEdit = questionData?.isEdit || questionCode;
        const res = isEdit
          ? await updateQuestionnaire(payloadData)
          : await postQuestionnaire(payloadData);
        const questionItem = res?.data?.length ? res.data[0] : null;
        console.log('🚀 ~ onSubmit ~ questionItem:', questionItem);

        await queryClient.invalidateQueries({
          queryKey: ['questionnaire-v3', 'all'],
        });

        if (questionItem?.final_question) {
          if (!isMilestoneCompleted(MILESTONES.QUESTIONNAIRE)) {
            await updateOnboarding({ milestone: MILESTONES.QUESTIONNAIRE });
          }
          if (
            !isMilestoneCompleted(MILESTONES.ONBOARDED) &&
            isMilestoneCompleted(MILESTONES.FIRST_HEALTH_MEASUREMENT)
          ) {
            await updateOnboarding({ milestone: MILESTONES.ONBOARDED });
          }
          setQuestionnaireData(undefined);
          return navigate(PATH_ROUTES.PROFILE);
        }

        if ('has_follow_up' in res && questionItem?.code) {
          return setQuestionnaireData(questionItem);
        }

        if (isEdit) {
          navigate(PATH_ROUTES.PROFILE);
          setQuestionnaireData(undefined);
          return;
        }

        setQuestionnaireData(questionItem);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsUpdating(false);
    }
  };
  return { onSubmit, isLoading: isUpdating };
}
